import { useCallback, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createRegistration,
  deleteRegistration,
  getRegistrationById,
  updateRegistration,
  updateRegistrationCancelOnly,
  updateRegistrationRestoreOnly,
} from "../../../../Services/Registration";
import {
  buildRepresentativePayment,
  createPayment,
  generatePaymentLink,
  getAllPaymentsByRegistrationId,
} from "../../../../Services/Payment";
import {
  STAFF_OVERRIDE_STATUS,
  type PaymentMethod,
  type PaymentStatus,
} from "../../../../Enums/RegistrationEnum";
import { IRegistration } from "../../../../interfaces/models/IRegistration";
import {
  IPayment,
  PAYMENT_CREATED_FROM,
} from "../../../../interfaces/models/IPayment";
import { resolveBillingFirmForRegistration } from "../../../../utils/resolveBillingFirmForRegistration";
import { formatStaffActorLabel } from "../../../../utils/auditActorDisplay";
import { toastError, toastPromise } from "../../../../utils/toast";
import {
  applyPrepaidFlagFromLedger,
  getPrepaidPaymentAmount,
  hasPrepaidPaymentInLedger,
  isPaymentMethodAllowed,
  isPaymentStatusAllowed,
  parsePaymentIncrement,
  resolveStaffPaymentIncrement,
  shouldCreateStaffPrepaidPayment,
  shouldEnsureStaffPrepaidPayment,
  validatePrepaidStaffPayment,
} from "../utils/participantPaymentRules";
import AuthContext from "../../../../store/AuthContext";
import type { AppDispatch } from "../../../../redux/store";
import {
  setActiveTab,
  setFirmData,
  setHasSavedOnce,
  setIsRegistrantModifyMode,
  setIsSaving,
  setPaymentData,
  setPayments,
  setRegistrationData,
} from "../../../../redux/states/editParticipant.state";
import {
  selectEditParticipantForm,
  selectEditParticipantSave,
  selectEventData,
  selectEventId,
  selectFirmData,
  selectIsEditMode,
  selectNewPaymentDate,
  selectAttendeeDisplayName,
  selectPayableBalance,
  selectRegistrationData,
} from "../editParticipant.selectors";
import { useRefreshAuditLogs } from "./useEditParticipantAudit";
import { useEditParticipantForm } from "./useEditParticipantForm";

function withPayment (
  payments: IPayment[],
  payment: IPayment | null | undefined,
): IPayment[] {
  if (!payment?.id) {
    return payments;
  }
  return [...payments.filter((item) => item.id !== payment.id), payment];
}

const saveToastOptions = { position: "top-center" as const };

function scrollContentToTop () {
  const scroll = () => {
    document.getElementById("content-container")?.scrollTo({ top: 0, left: 0 });
  };
  scroll();
  requestAnimationFrame(() => {
    requestAnimationFrame(scroll);
  });
}

export function useEditParticipantSave () {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loggedPerson } = useContext(AuthContext);

  const eventId = useSelector(selectEventId);
  const eventData = useSelector(selectEventData);
  const firmData = useSelector(selectFirmData);
  const registrationData = useSelector(selectRegistrationData);
  const attendeeDisplayName = useSelector(selectAttendeeDisplayName);
  const isEditMode = useSelector(selectIsEditMode);
  const payableBalance = useSelector(selectPayableBalance);
  const newPaymentDate = useSelector(selectNewPaymentDate);
  const { isSaving } = useSelector(selectEditParticipantSave);
  const { isRegistrantModifyMode, newPaymentAmount, newPaymentMethod } =
    useSelector(selectEditParticipantForm);

  const {
    prepareRegistrationPayload,
    syncFormFromRegistration,
    clearPaymentDrafts,
    validateGuestFields,
    setShowGuestValidationErrors,
  } = useEditParticipantForm();

  const refreshAuditLogs = useRefreshAuditLogs();

  const getActorLabel = useCallback(
    () => formatStaffActorLabel(loggedPerson?.name, loggedPerson?.lastName),
    [loggedPerson?.lastName, loggedPerson?.name],
  );

  const createStaffPaymentIfNeeded = useCallback(
    async (
      saved: IRegistration,
      paymentIncrement: number,
      paymentMethodOverride?: PaymentMethod,
    ): Promise<IPayment | null> => {
      const delta = Math.round(paymentIncrement * 100) / 100;
      if (Math.abs(delta) <= 0.009 || !saved.id) {
        return null;
      }
      if (delta < 0) {
        return null;
      }

      const paymentDateSec = newPaymentDate
        ? Math.floor(newPaymentDate.getTime() / 1000)
        : Math.floor(Date.now() / 1000);

      const paymentAttendeeType =
        saved.attendeeType === "sponsor" ||
        saved.attendeeType === "speaker" ||
        saved.attendeeType === "invited_guest"
          ? saved.attendeeType
          : "regular";

      const payment: IPayment = {
        registrationId: saved.id,
        eventId: saved.eventId,
        person: [
          {
            personId: saved.personId,
            attendeeType: paymentAttendeeType,
          },
        ],
        amount: delta,
        currency: eventData?.fee?.currency || "USD",
        paymentMethod:
          paymentMethodOverride ||
          (saved.paymentMethod as PaymentMethod) ||
          "creditCard",
        paymentDate: paymentDateSec,
        status: STAFF_OVERRIDE_STATUS as PaymentStatus,
        createdFrom: PAYMENT_CREATED_FROM.staffAttendee,
      };
      return createPayment(payment);
    },
    [eventData?.fee?.currency, newPaymentDate],
  );

  const ensureAutoPrepaidPayment = useCallback(
    async (
      saved: IRegistration,
      existingPayment: IPayment | null,
      knownPayments?: IPayment[],
    ): Promise<{ registration: IRegistration; payment: IPayment | null }> => {
      if (!saved.id || saved.isHostFirm) {
        return { registration: saved, payment: existingPayment };
      }

      const ledgerPayments =
        knownPayments ?? (await getAllPaymentsByRegistrationId(saved.id));
      const payments = existingPayment
        ? [
            ...ledgerPayments.filter((p) => p.id !== existingPayment.id),
            existingPayment,
          ]
        : ledgerPayments;

      if (
        !shouldEnsureStaffPrepaidPayment({
          registration: saved,
          event: eventData,
          payments,
        })
      ) {
        return { registration: saved, payment: existingPayment };
      }

      const prepaidAmount = getPrepaidPaymentAmount(eventData, saved);
      const prepaidPayment = await createStaffPaymentIfNeeded(
        saved,
        prepaidAmount,
        "prepaid",
      );
      if (!prepaidPayment) {
        return { registration: saved, payment: existingPayment };
      }

      const refreshed = saved.id
        ? await getRegistrationById(saved.id)
        : null;
      return {
        registration: refreshed || saved,
        payment: prepaidPayment,
      };
    },
    [createStaffPaymentIfNeeded, eventData],
  );

  const applySavedRegistrationState = useCallback(
    async (
      registrationResponse: IRegistration,
      paymentResponse: IPayment | null | undefined,
      options?: {
        clearPaymentDrafts?: boolean;
        exitRegistrantModifyMode?: boolean;
        knownPayments?: IPayment[];
      },
    ) => {
      const saved = registrationResponse;
      const firmId = saved.firmId?.trim();
      const firmAlreadyLoaded = Boolean(
        firmData && firmId && firmData.id === firmId,
      );
      const [payments, firm] = await Promise.all([
        options?.knownPayments
          ? Promise.resolve(options.knownPayments)
          : saved.id
            ? getAllPaymentsByRegistrationId(saved.id)
            : Promise.resolve([] as IPayment[]),
        firmAlreadyLoaded
          ? Promise.resolve(undefined)
          : resolveBillingFirmForRegistration(saved),
      ]);
      const nextRegistration = applyPrepaidFlagFromLedger(saved, payments);

      dispatch(setRegistrationData(nextRegistration));
      syncFormFromRegistration(nextRegistration);
      if (options?.clearPaymentDrafts) {
        clearPaymentDrafts();
      }
      dispatch(setHasSavedOnce(true));
      if (options?.exitRegistrantModifyMode) {
        dispatch(setIsRegistrantModifyMode(false));
      }

      dispatch(setPayments(payments));
      dispatch(
        setPaymentData(
          paymentResponse || buildRepresentativePayment(payments) || undefined,
        ),
      );

      if (firm) {
        dispatch(setFirmData(firm));
      }
      void refreshAuditLogs();
    },
    [
      clearPaymentDrafts,
      dispatch,
      firmData,
      refreshAuditLogs,
      syncFormFromRegistration,
    ],
  );

  const handleCancel = useCallback(() => {
    if (eventId) {
      navigate(`/events-staff/${eventId}/attendees`);
      return;
    }
    navigate(-1);
  }, [eventId, navigate]);

  const handleSaveRegistrantSection = useCallback(async () => {
    if (!registrationData?.id) {
      toastError("Registration is not loaded yet.");
      return;
    }
    if (!isRegistrantModifyMode) {
      toastError("Click Modify Registrant before saving registrant changes.");
      return;
    }

    const guestValidationError = validateGuestFields();
    if (guestValidationError) {
      setShowGuestValidationErrors(true);
      toastError(guestValidationError);
      return;
    }
    setShowGuestValidationErrors(false);

    const payload = prepareRegistrationPayload(
      "registrant",
      getActorLabel,
      loggedPerson?.id,
    );
    if (!payload?.id) {
      toastError("Unable to prepare registration data.");
      return;
    }

    dispatch(setIsSaving(true));
    try {
      const registrationResponse = await toastPromise(
        updateRegistration(payload),
        {
          pending: "Saving registrant information...",
          success: "Registrant information updated successfully!",
          error: (error: any) =>
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            "Failed to save registrant information.",
        },
        saveToastOptions,
      );
      if (!registrationResponse?.id) {
        throw new Error("Registration update failed - no ID returned");
      }
      await applySavedRegistrationState(registrationResponse, undefined, {
        exitRegistrantModifyMode: true,
      });
      scrollContentToTop();
    } catch (error) {
      console.error("Error saving registrant section:", error);
    } finally {
      dispatch(setIsSaving(false));
    }
  }, [
    applySavedRegistrationState,
    dispatch,
    getActorLabel,
    isRegistrantModifyMode,
    loggedPerson?.id,
    prepareRegistrationPayload,
    registrationData?.id,
    setShowGuestValidationErrors,
    validateGuestFields,
  ]);

  const handleSaveRegistration = useCallback(
    async (sendInvoice: boolean = false) => {
      if (!registrationData || !eventId) {
        toastError("Registration is not loaded yet.");
        return;
      }

      if (!registrationData.personId?.trim()) {
        toastError("Select a registrant before saving.");
        return;
      }

      const guestValidationError = validateGuestFields();
      if (guestValidationError) {
        setShowGuestValidationErrors(true);
        toastError(guestValidationError);
        return;
      }
      setShowGuestValidationErrors(false);

      const payload = prepareRegistrationPayload(
        "full",
        getActorLabel,
        loggedPerson?.id,
      );
      if (!payload) {
        toastError("Unable to prepare registration data.");
        return;
      }

      if (
        !isPaymentMethodAllowed(payload, eventData, payload.paymentMethod) ||
        !isPaymentStatusAllowed(payload, eventData, payload.paymentStatus)
      ) {
        toastError(
          "Payment method or status is not allowed for this registrant type.",
        );
        return;
      }

      const isCreate = !isEditMode || !registrationData.id;
      const registrantPaymentMethod = (
        payload.paymentMethod || registrationData.paymentMethod
      ) as PaymentMethod;
      // "0" means the draft was clamped/cleared when balance hit 0 — do not
      // auto-create a prepaid ledger row on this save.
      const paymentDraftZeroed = (newPaymentAmount ?? "").trim() === "0";
      const wantsPrepaidPayment =
        !paymentDraftZeroed &&
        (registrantPaymentMethod === "prepaid" ||
          newPaymentMethod === "prepaid");

      const existingPayments = registrationData.id
        ? await getAllPaymentsByRegistrationId(registrationData.id)
        : [];

      const creatingPrepaidPayment =
        wantsPrepaidPayment &&
        shouldCreateStaffPrepaidPayment({
          paymentMethod: "prepaid",
          payments: existingPayments,
        });

      if (creatingPrepaidPayment) {
        const prepaidValidation = validatePrepaidStaffPayment({
          paymentMethod: "prepaid",
          registration: registrationData,
          event: eventData,
          payments: existingPayments,
        });
        if (!prepaidValidation.ok) {
          toastError(prepaidValidation.message);
          return;
        }
      }

      const prepaidAmount = creatingPrepaidPayment
        ? resolveStaffPaymentIncrement({
          paymentMethod: "prepaid",
          typedAmount: newPaymentMethod === "prepaid" ? newPaymentAmount : "",
          registration: registrationData,
          event: eventData,
        })
        : null;

      const pendingPaymentAmount = parsePaymentIncrement(newPaymentAmount);

      if (pendingPaymentAmount != null && !newPaymentMethod) {
        toastError(
          "Select a payment method in New Payment before registering a payment amount.",
        );
        return;
      }

      const rawTyped = parseFloat((newPaymentAmount ?? "").trim());
      if (
        (newPaymentAmount ?? "").trim() !== "" &&
        !Number.isNaN(rawTyped) &&
        rawTyped < 0
      ) {
        toastError("Payment amount must be greater than zero.");
        return;
      }

      const typedIncrement = newPaymentMethod
        ? resolveStaffPaymentIncrement({
          paymentMethod: newPaymentMethod,
          typedAmount: newPaymentAmount,
          registration: registrationData,
          event: eventData,
        })
        : null;

      if (pendingPaymentAmount != null && typedIncrement == null) {
        toastError("Enter a valid payment amount greater than zero.");
        return;
      }

      const paymentIncrement = creatingPrepaidPayment
        ? prepaidAmount
        : typedIncrement;
      const paymentMethodForNewPayment: PaymentMethod | undefined =
        creatingPrepaidPayment
          ? "prepaid"
          : newPaymentMethod || undefined;

      if (creatingPrepaidPayment && prepaidAmount != null && prepaidAmount > 0) {
        const currentPaid =
          typeof registrationData.amountPaid === "number"
            ? registrationData.amountPaid
            : 0;
        payload.amountPaid =
          Math.round((currentPaid + prepaidAmount) * 100) / 100;
        payload.prepaid = true;
        payload.paymentMethod = "prepaid";
      } else if (hasPrepaidPaymentInLedger(existingPayments)) {
        payload.prepaid = true;
      }

      (payload as any).staffBypassPrepaidOfficeSlot = true;
      (payload as any).staffAdjustPrepaid = true;

      dispatch(setIsSaving(true));
      try {
        const { registrationResponse, paymentResponse, knownPayments } =
          await toastPromise(
          (async () => {
            if (isCreate) {
              const { id: _ignored, ...createPayload } = payload;
              const registrationPayload: IRegistration = {
                ...createPayload,
                createdFrom: "staff",
              };

              let registrationResponse =
                await createRegistration(registrationPayload);
              if (!registrationResponse?.id) {
                throw new Error(
                  "Registration creation failed - no ID returned",
                );
              }

              let paymentResponse: IPayment | null = null;
              if (
                !registrationResponse.isHostFirm &&
                paymentIncrement != null
              ) {
                paymentResponse = await createStaffPaymentIfNeeded(
                  {
                    ...registrationResponse,
                    ...registrationPayload,
                    id: registrationResponse.id,
                  },
                  paymentIncrement,
                  paymentMethodForNewPayment,
                );
              }

              if (paymentResponse) {
                const updated = await updateRegistration({
                  ...registrationResponse,
                  ...registrationPayload,
                  id: registrationResponse.id,
                } as IRegistration);
                if (updated) {
                  registrationResponse = updated;
                }
              }

              const ledgerForEnsure = withPayment([], paymentResponse);
              const ensured = paymentDraftZeroed
                ? {
                    registration: registrationResponse,
                    payment: paymentResponse,
                  }
                : await ensureAutoPrepaidPayment(
                    registrationResponse,
                    paymentResponse,
                    ledgerForEnsure,
                  );
              return {
                registrationResponse: ensured.registration,
                paymentResponse: ensured.payment,
                knownPayments: withPayment(ledgerForEnsure, ensured.payment),
              };
            }

            if (!payload.id) {
              throw new Error("Registration update failed - missing ID");
            }

            const paymentResponse =
              paymentIncrement != null
                ? await createStaffPaymentIfNeeded(
                  payload,
                  paymentIncrement,
                  paymentMethodForNewPayment,
                )
                : null;
            const registrationResponse = await updateRegistration(payload);
            if (!registrationResponse?.id) {
              throw new Error("Registration update failed - no ID returned");
            }
            const ledgerForEnsure = withPayment(existingPayments, paymentResponse);
            const ensured = paymentDraftZeroed
              ? {
                  registration: registrationResponse,
                  payment: paymentResponse,
                }
              : await ensureAutoPrepaidPayment(
                  registrationResponse,
                  paymentResponse,
                  ledgerForEnsure,
                );
            return {
              registrationResponse: ensured.registration,
              paymentResponse: ensured.payment,
              knownPayments: withPayment(ledgerForEnsure, ensured.payment),
            };
          })(),
          {
            pending: isCreate
              ? "Creating participant and payment information..."
              : "Saving participant and payment information...",
            success: isCreate
              ? "Participant and payment created successfully!"
              : "Participant and payment updated successfully!",
            error: (error: any) =>
              error?.response?.data?.error ||
              error?.response?.data?.message ||
              error?.message ||
              `Failed to ${isCreate ? "create" : "save"} participant information.`,
          },
          saveToastOptions,
        );

        await applySavedRegistrationState(
          registrationResponse,
          paymentResponse,
          {
            clearPaymentDrafts: true,
            exitRegistrantModifyMode: true,
            // Host-firm saves create a sponsor payment on the server after the
            // client ledger snapshot, so that list has to be read again.
            knownPayments: registrationResponse?.isHostFirm
              ? undefined
              : knownPayments,
          },
        );

        if (isCreate && registrationResponse?.id) {
          navigate(
            `/events-staff/${eventId}/attendees/Edit/${registrationResponse.id}`,
            {
              replace: true,
              ...(sendInvoice ? { state: { openTab: 1 } } : {}),
            },
          );
          scrollContentToTop();
          return;
        }

        if (sendInvoice) {
          dispatch(setActiveTab(1));
        }
        scrollContentToTop();
      } catch (error) {
        console.error("Error saving registration:", error);
      } finally {
        dispatch(setIsSaving(false));
      }
    },
    [
      applySavedRegistrationState,
      createStaffPaymentIfNeeded,
      dispatch,
      ensureAutoPrepaidPayment,
      eventData,
      eventId,
      getActorLabel,
      isEditMode,
      loggedPerson?.id,
      navigate,
      newPaymentAmount,
      newPaymentMethod,
      prepareRegistrationPayload,
      registrationData,
      setShowGuestValidationErrors,
      validateGuestFields,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (!registrationData?.id) {
      return;
    }
    try {
      await toastPromise(deleteRegistration(registrationData.id), {
        pending: "Deleting registration...",
        success: "Registration deleted.",
        error: "Failed to delete registration.",
      });
      handleCancel();
    } catch (error) {
      console.error("Error deleting registration:", error);
    }
  }, [handleCancel, registrationData?.id]);

  const handleCancelRegistration = useCallback(async (): Promise<boolean> => {
    if (!registrationData?.id) {
      return false;
    }

    setIsSaving(true);
    try {
      const registrationResponse = await toastPromise(
        updateRegistrationCancelOnly(
          registrationData.id,
          Math.floor(Date.now() / 1000),
          {
            changedBy: getActorLabel(),
            changedById: loggedPerson?.id,
          },
        ),
        {
          pending: "Cancelling registration...",
          success: "Registration cancelled.",
          error: (error: any) =>
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            "Failed to cancel registration.",
        },
      );
      if (!registrationResponse?.id) {
        throw new Error("Registration cancel failed - no ID returned");
      }
      await applySavedRegistrationState(registrationResponse, undefined, {
        exitRegistrantModifyMode: true,
      });
      return true;
    } catch (error) {
      console.error("Error cancelling registration:", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    applySavedRegistrationState,
    getActorLabel,
    loggedPerson?.id,
    registrationData?.id,
  ]);

  const handleRestoreRegistration = useCallback(async (): Promise<boolean> => {
    if (!registrationData?.id) {
      return false;
    }

    setIsSaving(true);
    try {
      const registrationResponse = await toastPromise(
        updateRegistrationRestoreOnly(registrationData.id, {
          changedBy: getActorLabel(),
          changedById: loggedPerson?.id,
        }),
        {
          pending: "Restoring registration...",
          success: "Registration restored.",
          error: (error: any) =>
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            "Failed to restore registration.",
        },
      );
      if (!registrationResponse?.id) {
        throw new Error("Registration restore failed - no ID returned");
      }
      await applySavedRegistrationState(registrationResponse, undefined, {
        exitRegistrantModifyMode: true,
      });
      return true;
    } catch (error) {
      console.error("Error restoring registration:", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    applySavedRegistrationState,
    getActorLabel,
    loggedPerson?.id,
    registrationData?.id,
  ]);

  const handleCopyPaymentLink = useCallback(async () => {
    if (!eventId || !registrationData?.id) {
      return;
    }
    const balance = Math.max(0, payableBalance);
    const eventName =
      eventData?.title?.trim() ||
      registrationData.eventName?.trim() ||
      "Event Registration";
    const registrantName =
      attendeeDisplayName ||
      registrationData.badgeName?.trim() ||
      "Registrant";
    const description = registrationData.shortId
      ? `${eventName} - ${registrationData.shortId}`
      : eventName;
    try {
      const link = await toastPromise(
        generatePaymentLink({
          amount: balance,
          currency: eventData?.fee?.currency || "USD",
          registrationId: registrationData.id,
          eventId,
          description,
          customFields: [
            {
              id: "custom_data-eventName",
              value: eventName,
            },
            {
              id: "custom_data-registrantName",
              value: registrantName,
            },
          ],
        }),
        {
          pending: "Generating payment link...",
          success: "Payment link copied to clipboard!",
          error: "Failed to generate payment link.",
        },
      );
      if (link) {
        await navigator.clipboard.writeText(link);
      }
    } catch (error) {
      console.error("Error copying payment link:", error);
    }
  }, [
    attendeeDisplayName,
    eventData?.fee?.currency,
    eventData?.title,
    eventId,
    payableBalance,
    registrationData?.badgeName,
    registrationData?.eventName,
    registrationData?.id,
    registrationData?.shortId,
  ]);

  return {
    isSaving,
    handleSaveRegistrantSection,
    handleSaveRegistration,
    handleDelete,
    handleCancelRegistration,
    handleRestoreRegistration,
    handleCopyPaymentLink,
    handleCancel,
  };
}
